import { ArrowLeft, GitMerge, Search } from "lucide-react";
import { useEffect, useMemo, useState, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import type { DuplicateMatch, MergePreview } from "@/api/types/clientDirectory";
import type { ForeignNational } from "@/api/types/foreignNational";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Skeleton } from "@/components/ui/skeleton";

import { getFullName } from "../../lib/format";
import { fieldLabel, formatValue, movesSummary } from "./mergeModel";

type Candidate = { id: string; name: string; detail: string };

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  client: ForeignNational;
  onMerged: () => void;
  /** Start on this record (from a duplicate flag). */
  initialSourceId?: string | null;
};

const fromDuplicate = (d: DuplicateMatch): Candidate => ({
  id: d.foreign_national_id,
  name: d.name,
  detail: [d.reason, d.email].filter(Boolean).join(" · ")
});

/**
 * Pick the duplicate record, decide each conflicting field, then merge it
 * into this client. This record is the one that stays.
 */
export const MergeClientDialog: FC<Props> = ({
  open,
  onOpenChange,
  client,
  onMerged,
  initialSourceId
}) => {
  const [suggested, setSuggested] = useState<Candidate[]>([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Candidate[]>([]);
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [takeSource, setTakeSource] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const name = getFullName(client) || "this client";

  useEffect(() => {
    if (!open) return undefined;
    setSourceId(initialSourceId ?? null);
    setPreview(null);
    setTakeSource(new Set());
    setQuery("");
    let live = true;
    ForeignNationalsAPI.findDuplicates({
      first_name: client.first_name,
      last_name: client.last_name,
      birthday: client.birthday?.slice(0, 10) || undefined,
      email: client.email || undefined,
      a_number: client.a_number || undefined,
      exclude_id: client.uid
    })
      .then((list) => live && setSuggested(list.map(fromDuplicate)))
      .catch(() => live && setSuggested([]));
    return () => {
      live = false;
    };
  }, [client, initialSourceId, open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setResults([]);
      return undefined;
    }
    let live = true;
    const t = window.setTimeout(() => {
      ForeignNationalsAPI.list({ q, limit: 8 })
        .then((res) => {
          if (!live) return;
          setResults(
            res.foreign_nationals
              .filter((fn) => fn.uid !== client.uid)
              .map((fn) => ({
                id: fn.uid,
                name: getFullName(fn) || "Unnamed client",
                detail: fn.email ?? ""
              }))
          );
        })
        .catch(() => live && setResults([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [client.uid, open, query]);

  useEffect(() => {
    if (!sourceId) return undefined;
    let live = true;
    setPreview(null);
    ForeignNationalsAPI.mergePreview(client.uid, sourceId)
      .then((p) => live && setPreview(p))
      .catch((e: unknown) => {
        if (!live) return;
        toast.error("Couldn't compare the records", {
          description: e instanceof Error ? e.message : undefined
        });
        setSourceId(null);
      });
    return () => {
      live = false;
    };
  }, [client.uid, sourceId]);

  const candidates = query.trim().length >= 2 ? results : suggested;

  const merge = async () => {
    if (!preview || busy) return;
    setBusy(true);
    try {
      const out = await ForeignNationalsAPI.merge(
        client.uid,
        preview.source.foreign_national_id,
        [...takeSource]
      );
      const moved = movesSummary({ ...out.copied, ...out.repointed });
      toast.success(`Merged ${preview.source.name} into ${name}`, {
        description: `${moved} moved here. The other record now redirects to this one.`
      });
      onOpenChange(false);
      onMerged();
    } catch (e) {
      toast.error("Couldn't merge", {
        description: e instanceof Error ? e.message : undefined
      });
    } finally {
      setBusy(false);
    }
  };

  const fills = useMemo(
    () => (preview?.fills ?? []).filter((f) => f.field !== "notes_md"),
    [preview]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Merge a duplicate into {name}</DialogTitle>
          <DialogDescription>
            This record stays. Cases, requests, documents and history from the
            other record move here, and the other record redirects to this one.
          </DialogDescription>
        </DialogHeader>

        {!sourceId ? (
          <div className="flex flex-col gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
              <Label htmlFor="merge-search" className="sr-only">
                Find the other record
              </Label>
              <Input
                id="merge-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email or A-Number"
                className="pl-8"
                autoFocus
              />
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              {query.trim().length >= 2
                ? "Matching clients"
                : "Likely duplicates"}
            </span>
            {candidates.length === 0 ? (
              <span className="text-sm text-muted-foreground">
                {query.trim().length >= 2
                  ? "No other clients match."
                  : "No likely duplicates. Search for the other record."}
              </span>
            ) : (
              <ul className="divide-y divide-border rounded-md border border-border">
                {candidates.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-fill-1"
                      onClick={() => setSourceId(c.id)}
                      data-testid="merge-candidate"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-medium">
                          {c.name}
                        </span>
                        {c.detail ? (
                          <span className="truncate text-xs text-muted-foreground">
                            {c.detail}
                          </span>
                        ) : null}
                      </span>
                      <span className="text-xs text-primary">Compare</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : !preview ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="rounded-md bg-fill-1 px-3 py-2 text-sm">
              Merging <span className="font-medium">{preview.source.name}</span>{" "}
              into <span className="font-medium">{preview.target.name}</span>:{" "}
              {movesSummary(preview.counts).toLowerCase()} will move.
            </div>

            {preview.conflicts.length > 0 ? (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">
                  Choose which value to keep
                </span>
                {preview.conflicts.map((c) => {
                  const pick = takeSource.has(c.field) ? "source" : "target";
                  return (
                    <fieldset
                      key={c.field}
                      className="rounded-md border border-border px-3 py-2"
                      data-testid="merge-conflict"
                    >
                      <legend className="px-1 text-xs font-medium text-muted-foreground">
                        {fieldLabel(c.field)}
                      </legend>
                      <RadioGroup
                        value={pick}
                        onValueChange={(v) =>
                          setTakeSource((prev) => {
                            const next = new Set(prev);
                            if (v === "source") next.add(c.field);
                            else next.delete(c.field);
                            return next;
                          })
                        }
                        className="gap-1.5"
                      >
                        {(["target", "source"] as const).map((side) => (
                          <div key={side} className="flex items-center gap-2">
                            <RadioGroupItem
                              value={side}
                              id={`merge-${c.field}-${side}`}
                            />
                            <Label
                              htmlFor={`merge-${c.field}-${side}`}
                              className="min-w-0 flex-1 font-normal"
                            >
                              <span className="truncate">
                                {formatValue(c[side])}
                              </span>
                              <span className="ml-2 text-xs text-muted-foreground">
                                {side === "target"
                                  ? "this record"
                                  : "other record"}
                              </span>
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </fieldset>
                  );
                })}
              </div>
            ) : (
              <span className="text-sm text-muted-foreground">
                No conflicting values.
              </span>
            )}

            {fills.length > 0 ? (
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium">
                  Filled in from the other record
                </span>
                <ul className="text-sm text-muted-foreground">
                  {fills.map((f) => (
                    <li key={f.field}>
                      {fieldLabel(f.field)}: {formatValue(f.value)}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {sourceId ? (
            <Button
              variant="ghost"
              onClick={() => setSourceId(null)}
              disabled={busy}
            >
              <ArrowLeft /> Pick another
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void merge()}
              disabled={!preview || busy}
              data-testid="merge-confirm"
            >
              <GitMerge /> {busy ? "Merging…" : "Merge records"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
